import { FunctionsHttpError } from '@supabase/supabase-js'
import { supabase } from './supabaseClient'

const TRAINER_INVITE_STORAGE_KEY = 'kilo_trainer_invite_token'
const TRAINER_INVITE_EMAIL_KEY = 'kilo_trainer_invite_email'

export type TrainerInviteRedeemResult =
  | { status: 'none' }
  | { status: 'linked' }
  | { status: 'already_linked' }
  | { status: 'wrong_account'; invitedEmail: string }
  | { status: 'expired' }
  | { status: 'error'; message: string }

async function readFunctionError(error: unknown): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    try {
      const payload = (await error.context.json()) as { error?: string; message?: string }
      if (payload?.error) return payload.error
      if (payload?.message) return payload.message
    } catch {
      /* ignore parse failure */
    }
  }
  if (error instanceof Error && error.message) return error.message
  return 'Request failed'
}

/** Reads ?trainer_invite= from the URL and persists token (+ optional invite_email) for later redemption. */
export function captureTrainerInviteFromUrl(): string | null {
  if (typeof window === 'undefined') return null

  const params = new URLSearchParams(window.location.search)
  const token = params.get('trainer_invite')?.trim()
  if (!token) return null

  const inviteEmail = params.get('invite_email')?.trim().toLowerCase()

  try {
    sessionStorage.setItem(TRAINER_INVITE_STORAGE_KEY, token)
    if (inviteEmail) {
      sessionStorage.setItem(TRAINER_INVITE_EMAIL_KEY, inviteEmail)
    }
  } catch {
    /* ignore storage failures */
  }

  params.delete('trainer_invite')
  params.delete('invite_email')
  const query = params.toString()
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}`
  window.history.replaceState(null, '', nextUrl)

  return inviteEmail ?? null
}

export function peekTrainerInviteToken(): string | null {
  try {
    return sessionStorage.getItem(TRAINER_INVITE_STORAGE_KEY)
  } catch {
    return null
  }
}

export function peekTrainerInviteEmail(): string | null {
  try {
    return sessionStorage.getItem(TRAINER_INVITE_EMAIL_KEY)
  } catch {
    return null
  }
}

export function clearTrainerInviteToken(): void {
  try {
    sessionStorage.removeItem(TRAINER_INVITE_STORAGE_KEY)
    sessionStorage.removeItem(TRAINER_INVITE_EMAIL_KEY)
  } catch {
    /* ignore */
  }
}

/** Redeem a pending coach invite for the current session, if any. */
export async function processPendingTrainerInvite(): Promise<TrainerInviteRedeemResult> {
  const token = peekTrainerInviteToken()
  if (!token) return { status: 'none' }

  const { data, error: invokeError } = await supabase.functions.invoke<{
    success?: boolean
    trainer_id?: string
    error?: string
  }>('accept-trainer-invite', { body: { token } })

  if (invokeError) {
    const message = await readFunctionError(invokeError)
    if (/expired/i.test(message)) return { status: 'expired' }
    if (/different email/i.test(message)) {
      return {
        status: 'wrong_account',
        invitedEmail: peekTrainerInviteEmail() ?? 'the invited email',
      }
    }
    return { status: 'error', message }
  }

  if (data?.error) {
    if (/expired/i.test(data.error)) return { status: 'expired' }
    if (/different email/i.test(data.error)) {
      return {
        status: 'wrong_account',
        invitedEmail: peekTrainerInviteEmail() ?? 'the invited email',
      }
    }
    return { status: 'error', message: data.error }
  }

  if (data?.success) {
    clearTrainerInviteToken()
    return { status: 'linked' }
  }

  return { status: 'error', message: 'Invite could not be redeemed' }
}
